import { useEffect } from "react";

/**
 * Marks the session as belonging to a campaign landing page, so the rest of the
 * app sends the visitor back THERE rather than to the main homepage.
 *
 * This is the same idea as the older auraMode flag that /market and /global
 * set, generalised. Those two stored a name ("market", "global") and every
 * consumer hard-coded a check against it, which does not extend: /usa, /uae,
 * /love, /horoscope and /india would each need another named branch. Storing
 * the PATH instead means one value works for every landing page and any future
 * one needs no code change here.
 *
 * Why it matters: these pages are bought with ad spend and have no relationship
 * to the homepage. A visitor who lands on /usa, signs in or taps the logo, and
 * ends up on the homepage has been taken off the page we paid to put them on,
 * along with whatever they had half-finished there.
 *
 * Session-scoped on purpose. It should follow this visit and not persist into a
 * later, unrelated one the way localStorage would.
 */
export const LANDING_PATHS = [
  "/india",
  "/usa",
  "/uae",
  "/love",
  "/horoscope",
];

/** Strips a trailing slash: Amplify 301s /usa to /usa/ and a bare === misses it. */
export const normalisePath = (p: string) => p.replace(/\/+$/, "") || "/";

/** Reads the landing path this session belongs to, if any. */
export function getLandingPath(): string | null {
  try {
    const v = sessionStorage.getItem("auraLanding");
    return v && LANDING_PATHS.includes(v) ? v : null;
  } catch {
    return null;
  }
}

/** Call from a landing page. Records that this session started there. */
export function useLandingMode(path: string) {
  useEffect(() => {
    try {
      sessionStorage.setItem("auraLanding", normalisePath(path));
    } catch {
      /* sessionStorage unavailable (private mode, storage blocked) */
    }
  }, [path]);
}
